import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft, User, BookOpen, TrendingUp, Clock, FileText, Save, Plus, Trash2, Edit3, Check,
  Send, ExternalLink, Star, Copy
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = async (path, token, options = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    method: options.method || 'GET',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
};

const LEVEL_LABELS = {
  sv: { beginner: 'Nybörjare', intermediate: 'Medel', advanced: 'Avancerad' },
  en: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
};
const PROGRESS_STATUS = {
  sv: { beginner: 'Nybörjare', improving: 'Förbättras', good: 'Bra', excellent: 'Utmärkt' },
  en: { beginner: 'Beginner', improving: 'Improving', good: 'Good', excellent: 'Excellent' },
};
const PROGRESS_COLORS = {
  beginner: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  improving: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  good: 'bg-green-500/20 text-green-400 border-green-500/30',
  excellent: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
};

const StudentDetail = ({ student, token, language, onBack, onRefresh }) => {
  const sv = language === 'sv';
  const [tab, setTab] = useState('info');
  const [placement, setPlacement] = useState([]);
  const [progress, setProgress] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [editForm, setEditForm] = useState({
    level: student.level || '', teacher: student.teacher || '', start_date: student.start_date || ''
  });
  const [progressForm, setProgressForm] = useState({
    current_book: '', current_page: 0, current_surah: '', tajweed_notes: '',
    teacher_feedback: '', homework: '', progress_status: 'beginner',
    memorization_new: '', memorization_revision: '', surahs_completed: []
  });
  const [lessonForm, setLessonForm] = useState({
    lesson_date: new Date().toISOString().slice(0, 10), duration: 60, teacher: student.teacher || '',
    what_was_read: '', tajweed_corrections: '', memorization_progress: '', homework_assigned: '', notes: '',
    attendance: 'present'
  });
  const [editingLesson, setEditingLesson] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [pl, pr, ls] = await Promise.all([
        api(`/admin/students/${student.id}/placement`, token),
        api(`/admin/students/${student.id}/progress`, token),
        api(`/admin/students/${student.id}/lessons`, token),
      ]);
      setPlacement(pl);
      setProgress(pr);
      setProgressForm({
        current_book: pr.current_book || '', current_page: pr.current_page || 0,
        current_surah: pr.current_surah || '', tajweed_notes: pr.tajweed_notes || '',
        teacher_feedback: pr.teacher_feedback || '', homework: pr.homework || '',
        progress_status: pr.progress_status || 'beginner',
        memorization_new: pr.memorization_new || '', memorization_revision: pr.memorization_revision || '',
        surahs_completed: pr.surahs_completed || []
      });
      setLessons(ls);
    } catch {}
  }, [student.id, token]);

  useEffect(() => { loadData(); }, [loadData]);

  const sendPlacementTest = async () => {
    const res = await api(`/admin/students/${student.id}/send-placement-test`, token, { method: 'POST' });
    const link = `${window.location.origin}/placement-test/${res.token}`;
    await navigator.clipboard.writeText(link).catch(() => {});
    alert(`${sv ? 'Länk kopierad' : 'Link copied'}:\n${link}`);
    loadData();
  };

  const assignLevel = async (level) => {
    await api(`/admin/students/${student.id}/assign-level`, token, { method: 'PUT', body: { assigned_level: level } });
    setEditForm(f => ({ ...f, level }));
    onRefresh();
    loadData();
  };

  const saveInfo = async () => {
    setSaving(true);
    await api(`/admin/students/${student.id}`, token, { method: 'PUT', body: editForm });
    setSaved('info'); setTimeout(() => setSaved(''), 2000);
    setSaving(false);
    onRefresh();
  };

  const saveProgress = async () => {
    setSaving(true);
    await api(`/admin/students/${student.id}/progress`, token, { method: 'PUT', body: progressForm });
    setSaved('progress'); setTimeout(() => setSaved(''), 2000);
    setSaving(false);
    loadData();
  };

  const addLesson = async () => {
    setSaving(true);
    await api('/admin/lessons', token, { method: 'POST', body: { ...lessonForm, student_id: student.id } });
    setLessonForm({ lesson_date: new Date().toISOString().slice(0, 10), duration: 60, teacher: student.teacher || '', what_was_read: '', tajweed_corrections: '', memorization_progress: '', homework_assigned: '', notes: '', attendance: 'present' });
    setSaved('lesson'); setTimeout(() => setSaved(''), 2000);
    setSaving(false);
    loadData();
  };

  const deleteLesson = async (id) => {
    if (!window.confirm(sv ? 'Ta bort lektionslogg?' : 'Delete lesson log?')) return;
    await api(`/admin/lessons/${id}`, token, { method: 'DELETE' });
    loadData();
  };

  const copyPortalLink = () => {
    if (student.portal_token) {
      const link = `${window.location.origin}/portal/${student.portal_token}`;
      navigator.clipboard.writeText(link).catch(() => {});
      alert(`${sv ? 'Portallänk kopierad' : 'Portal link copied'}:\n${link}`);
    }
  };

  const tabs = [
    { id: 'info', label: sv ? 'Profil' : 'Profile', icon: User },
    { id: 'placement', label: sv ? 'Nivåtest' : 'Placement', icon: BookOpen },
    { id: 'progress', label: sv ? 'Framsteg' : 'Progress', icon: TrendingUp },
    { id: 'lessons', label: sv ? 'Lektioner' : 'Lessons', icon: Clock },
  ];

  const ps = student.progress_status || 'beginner';

  return (
    <div data-testid="student-detail">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="p-2 rounded-lg hover:bg-gold/10 transition-colors" style={{ color: 'var(--text-secondary)' }} data-testid="back-to-students">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{student.name}</h2>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {student.level && <span className="text-xs px-2 py-0.5 rounded bg-gold/10 text-gold border border-gold/30">{LEVEL_LABELS[language]?.[student.level] || student.level}</span>}
            <span className={`text-xs px-2 py-0.5 rounded border ${PROGRESS_COLORS[ps] || ''}`}>{PROGRESS_STATUS[language]?.[ps] || ps}</span>
            {student.course && <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{student.course}</span>}
          </div>
        </div>
        {student.portal_token && (
          <button onClick={copyPortalLink} className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light" data-testid="copy-portal-link">
            <ExternalLink size={14} /> {sv ? 'Portallänk' : 'Portal Link'}
          </button>
        )}
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${active ? 'bg-gold text-black' : 'hover:bg-gold/10'}`} style={!active ? { color: 'var(--text-secondary)' } : {}} data-testid={`detail-tab-${t.id}`}>
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* INFO TAB */}
      {tab === 'info' && (
        <div className="space-y-4" data-testid="detail-info">
          <div className="pricing-card !p-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nivå' : 'Level'}</label>
                <select value={editForm.level} onChange={e => setEditForm(f => ({ ...f, level: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm">
                  <option value="">{sv ? 'Ej tilldelad' : 'Not assigned'}</option>
                  <option value="beginner">{LEVEL_LABELS[language]?.beginner}</option>
                  <option value="intermediate">{LEVEL_LABELS[language]?.intermediate}</option>
                  <option value="advanced">{LEVEL_LABELS[language]?.advanced}</option>
                </select>
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lärare' : 'Teacher'}</label>
                <input value={editForm.teacher} onChange={e => setEditForm(f => ({ ...f, teacher: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Startdatum' : 'Start Date'}</label>
              <input type="date" value={editForm.start_date} onChange={e => setEditForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
            </div>
            <button onClick={saveInfo} className="btn-gold w-full flex items-center justify-center gap-2 text-sm" disabled={saving} data-testid="save-student-info">
              {saved === 'info' ? <Check size={16} /> : <Save size={16} />} {saved === 'info' ? (sv ? 'Sparat!' : 'Saved!') : (sv ? 'Spara' : 'Save')}
            </button>
          </div>
          <div className="pricing-card !p-5">
            <p className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>{sv ? 'Kontaktinfo' : 'Contact Info'}</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{student.email || '-'} &middot; {student.phone || '-'} &middot; {sv ? 'Ålder' : 'Age'}: {student.age || '-'}</p>
            {student.notes && <p className="text-xs mt-2 italic" style={{ color: 'var(--text-muted)' }}>{student.notes}</p>}
          </div>
        </div>
      )}

      {/* PLACEMENT TAB */}
      {tab === 'placement' && (
        <div className="space-y-4" data-testid="detail-placement">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>{sv ? 'Nivåtest' : 'Placement Test'}</h3>
            <button onClick={sendPlacementTest} className="btn-gold !py-2 !px-4 flex items-center gap-2 text-sm" data-testid="send-placement-test">
              <Send size={14} /> {sv ? 'Skicka nytt test' : 'Send New Test'}
            </button>
          </div>
          {placement.length > 0 ? placement.map((p, i) => (
            <div key={i} className="pricing-card !p-5">
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs px-2 py-0.5 rounded ${p.completed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                  {p.completed ? (sv ? 'Slutfört' : 'Completed') : (sv ? 'Väntar' : 'Pending')}
                </span>
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{p.created_at ? new Date(p.created_at).toLocaleDateString('sv-SE') : ''}</span>
              </div>
              {p.completed && (
                <div className="space-y-3">
                  <div className="p-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Svar' : 'Answer'}</p>
                    <p className="text-sm" style={{ color: 'var(--text-primary)' }}>
                      {p.can_read_arabic === 'no' ? (sv ? 'Kan inte läsa arabiska' : "Can't read Arabic") :
                       p.can_read_arabic === 'slow' ? (sv ? 'Läser långsamt' : 'Reads slowly') :
                       (sv ? 'Kan läsa Koranen' : 'Can read Quran')}
                    </p>
                  </div>
                  <div className="p-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                    <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Rekommenderad nivå' : 'Recommended Level'}</p>
                    <p className="text-sm text-gold">{LEVEL_LABELS[language]?.[p.recommended_level] || p.recommended_level}</p>
                  </div>
                  {p.assigned_level && (
                    <div className="p-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Tilldelad nivå' : 'Assigned Level'}</p>
                      <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{LEVEL_LABELS[language]?.[p.assigned_level] || p.assigned_level}</p>
                    </div>
                  )}
                  {p.teacher_evaluation && (
                    <div className="p-3 rounded" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lärarens utvärdering' : "Teacher's Evaluation"}</p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{p.teacher_evaluation}</p>
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{sv ? 'Tilldela nivå:' : 'Assign level:'}</p>
                    {['beginner', 'intermediate', 'advanced'].map(l => (
                      <button key={l} onClick={() => assignLevel(l)} className="text-xs px-2 py-1 rounded transition-colors hover:bg-gold/10 border" style={{ borderColor: 'var(--border-color)', color: 'var(--text-secondary)' }} data-testid={`assign-${l}`}>
                        {LEVEL_LABELS[language]?.[l]}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {!p.completed && (
                <div className="flex items-center gap-2">
                  <button onClick={() => { const link = `${window.location.origin}/placement-test/${p.token}`; navigator.clipboard.writeText(link).catch(() => {}); alert(`${sv ? 'Länk kopierad' : 'Link copied'}:\n${link}`); }}
                    className="flex items-center gap-1 text-xs text-gold hover:text-gold-light">
                    <Copy size={12} /> {sv ? 'Kopiera länk' : 'Copy link'}
                  </button>
                </div>
              )}
            </div>
          )) : (
            <div className="pricing-card !p-8 text-center">
              <p style={{ color: 'var(--text-muted)' }}>{sv ? 'Inget nivåtest skickat ännu.' : 'No placement test sent yet.'}</p>
            </div>
          )}
        </div>
      )}

      {/* PROGRESS TAB */}
      {tab === 'progress' && (
        <div className="space-y-4" data-testid="detail-progress">
          <div className="pricing-card !p-5 space-y-4">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><TrendingUp size={16} className="text-gold" /> {sv ? 'Studieframsteg' : 'Study Progress'}</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Status' : 'Status'}</label>
                <select value={progressForm.progress_status} onChange={e => setProgressForm(f => ({ ...f, progress_status: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm">
                  {Object.entries(PROGRESS_STATUS[language] || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nuvarande bok' : 'Current Book'}</label>
                <input value={progressForm.current_book} onChange={e => setProgressForm(f => ({ ...f, current_book: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" placeholder="Quran Online Method / Quran / Hifdh" />
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nuvarande sida' : 'Current Page'}</label>
                <input type="number" value={progressForm.current_page} onChange={e => setProgressForm(f => ({ ...f, current_page: parseInt(e.target.value) || 0 }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Nuvarande surah' : 'Current Surah'}</label>
                <input value={progressForm.current_surah} onChange={e => setProgressForm(f => ({ ...f, current_surah: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Tajweed-anteckningar' : 'Tajweed Notes'}</label>
              <textarea value={progressForm.tajweed_notes} onChange={e => setProgressForm(f => ({ ...f, tajweed_notes: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lärarens feedback' : "Teacher's Feedback"}</label>
              <textarea value={progressForm.teacher_feedback} onChange={e => setProgressForm(f => ({ ...f, teacher_feedback: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läxa' : 'Homework'}</label>
              <textarea value={progressForm.homework} onChange={e => setProgressForm(f => ({ ...f, homework: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} />
            </div>
            <h4 className="font-medium text-sm pt-2" style={{ color: 'var(--text-primary)' }}>{sv ? 'Memorering (Hifdh)' : 'Memorization (Hifdh)'}</h4>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Ny memorering' : 'New Memorization'}</label>
                <input value={progressForm.memorization_new} onChange={e => setProgressForm(f => ({ ...f, memorization_new: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Revision' : 'Revision'}</label>
                <input value={progressForm.memorization_revision} onChange={e => setProgressForm(f => ({ ...f, memorization_revision: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
            </div>
            <button onClick={saveProgress} className="btn-gold w-full flex items-center justify-center gap-2 text-sm" disabled={saving} data-testid="save-progress">
              {saved === 'progress' ? <Check size={16} /> : <Save size={16} />} {saved === 'progress' ? (sv ? 'Sparat!' : 'Saved!') : (sv ? 'Spara framsteg' : 'Save Progress')}
            </button>
          </div>
        </div>
      )}

      {/* LESSONS TAB */}
      {tab === 'lessons' && (
        <div className="space-y-4" data-testid="detail-lessons">
          {/* Add Lesson Form */}
          <div className="pricing-card !p-5 space-y-3">
            <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><Plus size={16} className="text-gold" /> {sv ? 'Ny lektionslogg' : 'New Lesson Log'}</h3>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Datum' : 'Date'}</label>
                <input type="date" value={lessonForm.lesson_date} onChange={e => setLessonForm(f => ({ ...f, lesson_date: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Längd (min)' : 'Duration (min)'}</label>
                <input type="number" value={lessonForm.duration} onChange={e => setLessonForm(f => ({ ...f, duration: parseInt(e.target.value) || 60 }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
              <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lärare' : 'Teacher'}</label>
                <input value={lessonForm.teacher} onChange={e => setLessonForm(f => ({ ...f, teacher: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
              </div>
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Vad lästes' : 'What was read'}</label>
              <input value={lessonForm.what_was_read} onChange={e => setLessonForm(f => ({ ...f, what_was_read: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Tajweed-korrigeringar' : 'Tajweed Corrections'}</label>
              <input value={lessonForm.tajweed_corrections} onChange={e => setLessonForm(f => ({ ...f, tajweed_corrections: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Memoreringsframsteg' : 'Memorization Progress'}</label>
              <input value={lessonForm.memorization_progress} onChange={e => setLessonForm(f => ({ ...f, memorization_progress: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Läxa till nästa lektion' : 'Homework for next lesson'}</label>
              <input value={lessonForm.homework_assigned} onChange={e => setLessonForm(f => ({ ...f, homework_assigned: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm" />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Anteckningar' : 'Notes'}</label>
              <textarea value={lessonForm.notes} onChange={e => setLessonForm(f => ({ ...f, notes: e.target.value }))} className="w-full px-3 py-2 rounded-lg text-sm resize-none" rows={2} />
            </div>
            <div><label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Närvaro' : 'Attendance'}</label>
              <div className="flex gap-2">
                {[{ v: 'present', l: sv ? 'Närvarande' : 'Present', c: 'bg-green-500/20 text-green-400 border-green-500/30' },
                  { v: 'late', l: sv ? 'Sen' : 'Late', c: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
                  { v: 'absent', l: sv ? 'Frånvarande' : 'Absent', c: 'bg-red-500/20 text-red-400 border-red-500/30' }
                ].map(a => (
                  <button key={a.v} type="button" onClick={() => setLessonForm(f => ({ ...f, attendance: a.v }))}
                    className={`px-3 py-1.5 rounded text-xs font-medium border transition-all ${lessonForm.attendance === a.v ? a.c : ''}`}
                    style={lessonForm.attendance !== a.v ? { color: 'var(--text-muted)', borderColor: 'var(--border-color)' } : {}}
                    data-testid={`lesson-att-${a.v}`}>{a.l}</button>
                ))}
              </div>
            </div>
            <button onClick={addLesson} className="btn-gold w-full flex items-center justify-center gap-2 text-sm" disabled={saving} data-testid="add-lesson-log">
              {saved === 'lesson' ? <Check size={16} /> : <Plus size={16} />} {saved === 'lesson' ? (sv ? 'Sparad!' : 'Saved!') : (sv ? 'Spara lektion' : 'Save Lesson')}
            </button>
          </div>

          {/* Lesson History */}
          <h3 className="font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}><Clock size={16} className="text-gold" /> {sv ? 'Lektionshistorik' : 'Lesson History'}</h3>
          {lessons.map(l => (
            <div key={l.id} className="pricing-card !p-4" data-testid={`lesson-${l.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{l.lesson_date}</span>
                  {l.attendance && l.attendance !== 'present' && (
                    <span className={`text-xs px-2 py-0.5 rounded ${l.attendance === 'late' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-red-500/15 text-red-400'}`}>
                      {l.attendance === 'late' ? (sv ? 'Sen' : 'Late') : (sv ? 'Frånvarande' : 'Absent')}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>{l.duration} min</span>
                  <button onClick={() => deleteLesson(l.id)} className="p-1 rounded hover:bg-red-500/10 text-red-400"><Trash2 size={12} /></button>
                </div>
              </div>
              {l.teacher && <p className="text-xs mb-1" style={{ color: 'var(--text-muted)' }}>{sv ? 'Lärare' : 'Teacher'}: {l.teacher}</p>}
              {l.what_was_read && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold text-xs">{sv ? 'Läst: ' : 'Read: '}</span>{l.what_was_read}</p>}
              {l.tajweed_corrections && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold text-xs">Tajweed: </span>{l.tajweed_corrections}</p>}
              {l.memorization_progress && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold text-xs">{sv ? 'Memorering: ' : 'Memorization: '}</span>{l.memorization_progress}</p>}
              {l.homework_assigned && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}><span className="text-gold text-xs">{sv ? 'Läxa: ' : 'Homework: '}</span>{l.homework_assigned}</p>}
              {l.notes && <p className="text-xs mt-1 italic" style={{ color: 'var(--text-muted)' }}>{l.notes}</p>}
            </div>
          ))}
          {lessons.length === 0 && <p className="text-center py-4" style={{ color: 'var(--text-muted)' }}>{sv ? 'Inga lektioner ännu.' : 'No lessons yet.'}</p>}
        </div>
      )}
    </div>
  );
};

export default StudentDetail;
